import { useState, useEffect } from 'react';

interface PWAInstallHook {
  shouldShow: boolean;
  mode: 'ios' | 'android' | null;
  promptInstall: () => void;
  dismiss: () => void;
  remindLater: () => void;
  isAddedToHomeScreen: boolean;
  isStandaloneMode: boolean;
  resetState: () => void;
  browserType: 'safari' | 'chrome' | 'other';
}

export const usePWAInstall = (): PWAInstallHook => {
  const [shouldShow, setShouldShow] = useState(false);
  const [mode, setMode] = useState<'ios' | 'android' | null>(null);
  const [isAddedToHomeScreen, setIsAddedToHomeScreen] = useState(false);
  const [isStandaloneMode, setIsStandaloneMode] = useState(false);
  const [browserType, setBrowserType] = useState<'safari' | 'chrome' | 'other'>(
    'other'
  );

  // Enhanced device detection
  const isIPad = () => {
    return (
      /iPad/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  };

  const isIOSChrome = () => {
    return (
      /iPhone|iPad|iPod/.test(navigator.userAgent) &&
      /CriOS/.test(navigator.userAgent)
    );
  };

  const isIOSSafari = () => {
    return isIPad() && /Safari/.test(navigator.userAgent) && !isIOSChrome();
  };

  const checkStandaloneMode = () => {
    // Enhanced standalone detection
    const navigatorStandalone = (window.navigator as any).standalone === true;
    const displayModeStandalone = window.matchMedia(
      '(display-mode: standalone)'
    ).matches;

    return navigatorStandalone || displayModeStandalone;
  };

  const checkIsAddedToHomeScreen = () => {
    const currentlyStandalone = checkStandaloneMode();
    const urlParams = new URLSearchParams(window.location.search);
    const hasInstalledParam = urlParams.get('installed') === 'true';

    // Definitive detection - app is running in standalone mode
    if (currentlyStandalone || hasInstalledParam) {
      return true;
    }

    // Check if previously detected as installed (within same context)
    const addedToHomeScreen =
      localStorage.getItem('addedToHomeScreen') === 'true';
    const lastAccess = localStorage.getItem('lastStandaloneAccess');

    if (addedToHomeScreen && lastAccess) {
      const daysSinceAccess =
        (Date.now() - parseInt(lastAccess)) / (1000 * 60 * 60 * 24);
      return daysSinceAccess <= 30; // Consider installed if accessed within 30 days
    }

    return false;
  };

  const markAsAddedToHomeScreen = () => {
    localStorage.setItem('addedToHomeScreen', 'true');
    localStorage.setItem('lastStandaloneAccess', Date.now().toString());
    setIsAddedToHomeScreen(true);
  };

  const getBrowserType = () => {
    if (isIOSSafari()) return 'safari';
    if (isIOSChrome()) return 'chrome';
    return 'other';
  };

  // Check if popup was permanently dismissed
  const isPermanentlyDismissed = () => {
    return localStorage.getItem('pwa-install-permanently-dismissed') === 'true';
  };

  // Check if popup should be shown this session (remind later scenario)
  const isRemindedLater = () => {
    return sessionStorage.getItem('pwa-install-remind-later') === 'true';
  };

  useEffect(() => {
    const currentlyStandalone = checkStandaloneMode();
    const urlParams = new URLSearchParams(window.location.search);
    const hasInstalledParam = urlParams.get('installed') === 'true';
    const detectedBrowserType = getBrowserType();

    setIsStandaloneMode(currentlyStandalone);
    setBrowserType(detectedBrowserType);

    // Clean URL if has installed parameter
    if (hasInstalledParam) {
      const url = new URL(window.location.href);
      url.searchParams.delete('installed');
      window.history.replaceState({}, document.title, url.toString());
    }

    // Mark as installed if detected
    if (currentlyStandalone || hasInstalledParam) {
      markAsAddedToHomeScreen();
    }

    const addedToHomeScreen = checkIsAddedToHomeScreen();
    setIsAddedToHomeScreen(addedToHomeScreen);

    // CRITICAL: Never show popup in standalone mode
    if (currentlyStandalone) {
      setShouldShow(false);
      setMode(null);
      return;
    }

    // Show popup logic with enhanced conditions
    const permanentlyDismissed = isPermanentlyDismissed();
    const remindedLater = isRemindedLater();

    const shouldShowPopup =
      isIPad() &&
      !addedToHomeScreen &&
      !permanentlyDismissed &&
      !remindedLater &&
      !currentlyStandalone; // Extra check for standalone

    if (shouldShowPopup) {
      setMode('ios');
      setShouldShow(true);
    }

    console.log('PWA Install Status:', {
      shouldShow: shouldShowPopup,
      isStandalone: currentlyStandalone,
      isInstalled: addedToHomeScreen,
      browserType: detectedBrowserType,
      permanentlyDismissed,
      remindedLater,
    });
  }, []);

  // Dismiss permanently - user chose not to be reminded again
  const dismiss = () => {
    setShouldShow(false);
    localStorage.setItem('pwa-install-permanently-dismissed', 'true');
    sessionStorage.removeItem('pwa-install-remind-later');
  };

  // Remind later - user wants to see popup again on next visit
  const remindLater = () => {
    setShouldShow(false);
    sessionStorage.setItem('pwa-install-remind-later', 'true');
  };

  const resetState = () => {
    localStorage.removeItem('addedToHomeScreen');
    localStorage.removeItem('lastStandaloneAccess');
    localStorage.removeItem('pwa-install-permanently-dismissed');
    sessionStorage.removeItem('pwa-install-remind-later');
    setIsAddedToHomeScreen(false);
    setIsStandaloneMode(false);
    setShouldShow(false);
  };

  return {
    shouldShow,
    mode,
    promptInstall: remindLater, // Changed to remindLater for clarity
    dismiss,
    remindLater,
    isAddedToHomeScreen,
    isStandaloneMode,
    resetState,
    browserType,
  };
};
